import { Controller } from "@hotwired/stimulus"

// Reads one category off a chart and quotes it into the readout line above the
// plot.
//
// ## What the server already sent, and why this only ever quotes it
//
// The widget arrives finished: every mark is drawn, and every figure is in the
// `<details>` table underneath in words. That table is the accessible path and
// the no-script path both, so nothing here may be the only route to a value.
// This controller adds no number, fetches no number and stores no number. It
// moves text that is already on the page into a line that is already on the
// page.
//
// Three rules follow from that, and each one is a rule rather than a taste.
//
//   1. **The readout is filled FROM THE TABLE ROW.** Not from a second copy of
//      the figures in data attributes. There is one statement of each category
//      in the document, the one the table renders, and the chart quotes it. Two
//      copies drift, and the copy that drifts is the one nobody is looking at.
//      On this product that is not an inconvenience: a figure this page cannot
//      stand behind is the whole thing it sells. Same discipline as
//      `layoff_sources_controller.js`, which moves a row's citation list rather
//      than copying it.
//   2. **One default is prevented, and only one.** A horizontal arrow on the
//      track gives up its pixel scroll for a band step, and that call sits
//      after every early return, so it never swallows a key that then does
//      nothing. Everything else is left alone: a default taken by a handler
//      that shows nothing is worse than the plain page, and a mark that is one
//      day wrapped in a link keeps working.
//   3. **No `tabindex` and no ARIA go onto the picture.** `aria-hidden="true"`
//      must not wrap a focusable element, so the first focusable mark forces the
//      whole accessible design to be rebuilt around the graphic, and what it
//      would buy is a row of stops that read out a table that is one Tab away
//      and richer. The scroll container's own `tabindex` belongs to the
//      stylesheet and the template, and it is for scrolling, not for the marks.
//
// ## The keyboard cursor, and why it is the pin rather than a third state
//
// A chart a mouse can read and a keyboard cannot is Chartability's one critical
// operable failure, and until this landed, reading a value without a pointer
// meant opening the figures table. The track is already focusable and already
// named, so the cursor binds THERE and the picture gains no tab stop. That is
// not tidiness: `aria-hidden` may not wrap a focusable element, so the first
// focusable mark forces the whole accessible design to be rebuilt around a
// graphic that says nothing, in exchange for a row of stops reading out a table
// that is one Tab away and richer.
//
// The cursor sets the SAME `pinnedIndex` a tap sets. One state machine, one set
// of classes, and no third appearance for the stylesheet to invent: a keyboard
// cursor and a pinned selection are the same idea. Left and Right step it, Home
// and End take the ends, and Escape clears it through the handler that was
// already here.
//
// Four keys are prevented and no others. The two horizontal arrows trade the
// browser's pixel scroll for a band step that scrolls as well, which is a
// superset rather than a loss: every band is reachable, both ends in one key,
// and the figures arrive with the view. Home and End are prevented for a
// second reason, measured rather than assumed: with focus in the track their
// default is a jump to the end of the DOCUMENT, and that jump cancels the
// scroll the step just asked for. Left native, End selects the newest month
// and leaves the reader at the footer with it four thousand pixels above them,
// which is the feature not working rather than the feature costing something.
//
// ArrowUp, ArrowDown and the Page keys stay native, and they are what the rule
// is actually for: a reader must still be able to scroll the page with focus
// parked in the chart. So does any of the four carrying a modifier, because
// Command plus Left is the browser's back button and Control plus Home is the
// top of the document, and neither is ours to take.
//
// The track is `direction: rtl`, which is how it opens on the most recent month
// rather than on one two years old. Only the scroll origin moves: the drawing
// resets `direction` and the bands are still laid out oldest first, so
// ArrowRight is index + 1 and the LATER month. That was checked in a browser
// rather than reasoned about, because it is the half of this that ships
// backwards.
//
// Discovery goes in the resting sentence, which is always in the document. A
// hint revealed on focus would be content on hover or focus under WCAG 2.2 SC
// 1.4.13, with dismissable, hoverable and persistent all to satisfy; a sentence
// that was there the whole time owes that criterion nothing.
//
// ## The cue outside the plot, and why it is an attribute rather than a class
//
// A selection paints the band, the column and the row, and at the moment of the
// tap that made it the band and the column are under the reader's own hand. So
// the selection says itself once more on the category's axis label, which is
// outside the plot and beside the finger rather than beneath it.
//
// The axis prints eight labels for twenty four categories, because an axis that
// prints all of them is unreadable, so two thirds of the categories had no label
// to mark and the cue was missing exactly where a reader starts: the newest
// month, which is what a phone opens on, what the first arrow press takes and
// what `End` takes. The server ships a label for those categories too, and it
// carries the `hidden` attribute.
//
// So this controller reveals a per-category element the server shipped `hidden`
// while that category is selected, and hides it again when the selection moves.
// Stated that generally on purpose: it is the same load-bearing `hidden` the
// readout itself ships with, it needs no new target and no new class, and the
// next per-category aside that should only exist while a reader is asking gets
// it by shipping hidden. What is revealed is text the server wrote, in the place
// the server put it. Nothing here composes a label.
//
// Which of them ship hidden is read ONCE, at connect, before anything has been
// revealed. Read later it would be a snapshot of this controller's own work.
//
// Selection only, never hover. A pointer fine enough to hover is not covering
// anything, and an axis that grows a label under the pointer and drops it again
// is an axis that flickers on the way past.
//
// ## Why the readout is a fixed line and not a tooltip
//
// WCAG 2.2 SC 1.4.13 governs content that APPEARS on hover or focus, and with
// it the dismissable, hoverable and persistent obligations that hand-rolled
// tooltips habitually fail. A line that is always in the document and only
// changes its text is not additional content, so the criterion does not apply
// at all. It also cannot be clipped by the `overflow-x: auto` plot box, cannot
// cover the mark being compared against, and needs no positioning engine that
// re-runs on scroll. CSS anchor positioning is not Baseline and Safari lacks
// the flip rules that are its whole point, and `popover="hint"` has no Safari
// support at all, so the bubble shapes are not available in 2026 anyway.
//
// ## Why the readout is hidden from assistive technology
//
// The readout is a copy of table rows that are already announced, not a
// supplement to them. Exposed as a live region it fires on every `pointerover`,
// which is a firehose for a screen reader user who is also holding a mouse.
// Exposed as a plain paragraph it is text that silently differs from what was
// announced when the reader passed it. The table is the accessible path, so
// this line is marked `aria-hidden` and stays a purely visual echo.
export default class extends Controller {
  // `focusLabel`, `focusFigure` and `focusNote` are optional, and live in a
  // caller's own summary block rather than in the widget. While a category is
  // pointed at they show its row's label, figure and brief; at rest they show
  // whatever the server put in them. See `focus()`.
  static targets = ["readout", "hit", "row", "track", "focusLabel", "focusFigure", "focusNote",
                    "pager", "earlier", "later"]
  // `frame`, `param` and `selected` are set only on a chart whose categories
  // lead somewhere: selecting one loads its address into that Turbo Frame,
  // `param` is the query parameter the address sets, and `selected` is the
  // category the page was served with, -1 for none.
  static values = { resting: String, frame: String, param: String, selected: { type: Number, default: -1 } }

  // How long the arrow keys may rest on a category before the list follows
  // it. Stepping through twelve months is twelve keypresses, and twelve
  // requests for lists nobody stopped to read is the wrong price for it.
  static FOLLOW_DELAY = 350

  // Put on EVERY element carrying the category's index, not on the first one
  // found: the hit area, the table row, the axis label, and however many visible
  // marks the component stamped. A column is a stack of segments and a category
  // can carry a ring as well, so "the mark" has not been one node for some time
  // and a highlight that reached one storey would light half a column. What any
  // of it looks like is the stylesheet's, and so is whether the hover one is
  // drawn at all on a given pointer type.
  static ACTIVE_CLASS = "is-active"
  static PINNED_CLASS = "is-pinned"

  // On the readout while it is quoting a category, off while it is resting. The
  // line is the one piece of copy in this block whose content changes, and this
  // is what lets the stylesheet tell an answer from an invitation without a
  // second type size.
  static READING_CLASS = "is-reading"

  // A phone reports `hover: none`, and browsers there emulate hover by spending
  // the first tap on `:hover`. The stylesheet guards the hover STYLES with
  // `@media (hover: hover) and (pointer: fine)`; this guards the hover
  // BEHAVIOUR with the same query, so a tap on a touch screen goes straight to
  // the click handler and selects, rather than leaving a category showing that
  // no pointer is anywhere near.
  static HOVER_QUERY = "(hover: hover) and (pointer: fine)"

  // Physical keys against category order, which is the same thing here: the
  // track's `rtl` moves the scroll origin and nothing about the layout, so
  // Right is the later category at every writing direction this page has.
  static STEPS = { ArrowRight: 1, ArrowLeft: -1 }
  static FIRST_KEY = "Home"
  static LAST_KEY = "End"

  // A middle dot rather than markup, so the line reads correctly with no
  // stylesheet at all. The fields themselves are whatever the table renders,
  // in the table's own order and the table's own words, which is how a chart
  // that knows nothing about its subject stays honest about it: "Not known"
  // arrives here as "Not known" and never as a nought.
  static SEPARATOR = " · "

  connect() {
    // Paging comes first and stands alone: it is about the track, and a chart
    // with no readout still has a track a desktop reader cannot otherwise get
    // to the far end of.
    this.connectPager()

    // Without a readout or a summary block there is nothing to fill, so bind
    // nothing. An early return here leaves a page that behaves exactly like the
    // server response, which is the correct failure.
    if (!this.hasReadoutTarget && !this.hasFocusLabelTarget) return

    // The category under the pointer, and the category a click selected. Kept
    // apart because a pointer leaving the plot must fall back to the selection
    // rather than to the resting sentence, and Escape must clear the selection
    // without waiting for a pointer to move.
    this.hoveredIndex = null
    this.pinnedIndex = null
    this.hoverQuery = window.matchMedia(this.constructor.HOVER_QUERY)
    // Read here and nowhere else. After the first reveal the document no longer
    // says which of these the server sent hidden, and a set rebuilt later would
    // be this controller reading its own handiwork back as if it were markup.
    this.asides = this.hiddenIndexedElements()
    // Held as well as looked up, because Stimulus has taken the scope's target
    // lookups away by the time `disconnect` runs and the line still has to be
    // put back to its resting sentence there.
    this.readout = this.hasReadoutTarget ? this.readoutTarget : null
    // The summary block's own words, held so that leaving a category puts back
    // exactly what the server sent rather than something rebuilt here.
    this.focusSlots = ["Label", "Figure", "Note"]
      .filter((name) => this[`hasFocus${name}Target`])
      .map((name) => {
        const element = this[`focus${name}Target`]
        return { name, element, resting: element.textContent }
      })

    this.onPointerOver = this.onPointerOver.bind(this)
    this.onPointerLeave = this.onPointerLeave.bind(this)
    this.onClick = this.onClick.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onTrackKeyDown = this.onTrackKeyDown.bind(this)

    // Delegated on the widget root rather than bound per mark, so the component
    // is not asked to name this controller's methods in `data-action` on every
    // hit rect and every table row. The frozen DOM contract is the index and
    // the three targets; the wiring is this file's business.
    this.element.addEventListener("pointerover", this.onPointerOver)
    this.element.addEventListener("pointerleave", this.onPointerLeave)
    this.element.addEventListener("click", this.onClick)
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeyDown)

    // On the track and never on `document`. Arrow keys belong to whatever holds
    // focus, and a chart that steps its cursor while a reader is elsewhere on
    // the page is a chart that has taken the page over. Held as well as looked
    // up for the reason the readout is: the target lookup is gone by the time
    // `disconnect` runs and the listener still has to come off.
    if (this.hasTrackTarget) {
      this.track = this.trackTarget
      this.track.addEventListener("keydown", this.onTrackKeyDown)
    }

    // Announced by nobody, for the reason in the header, and set here as well
    // as in the template so the invariant holds whatever markup arrives.
    // The template ships this line `hidden`, so a reader with no bundle never
    // meets a sentence inviting them into a behaviour that is not running. It
    // becomes visible at the moment the behaviour exists and not before.
    if (this.readout) {
      this.readout.setAttribute("aria-hidden", "true")
      this.readout.hidden = false
    }

    this.onFrameLoad = this.onFrameLoad.bind(this)
    if (this.linked()) {
      document.addEventListener("turbo:frame-load", this.onFrameLoad)
      if (this.selectedValue >= 0) this.pinnedIndex = this.selectedValue
    }

    this.render()
    if (this.pinnedIndex !== null) this.scrollToIndex(this.pinnedIndex)
  }

  disconnect() {
    this.disconnectPager()
    if (this.onFrameLoad) document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    clearTimeout(this.followTimer)
    this.element.removeEventListener("pointerover", this.onPointerOver)
    this.element.removeEventListener("pointerleave", this.onPointerLeave)
    this.element.removeEventListener("click", this.onClick)
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeyDown)
    this.track?.removeEventListener("keydown", this.onTrackKeyDown)
    this.track = null

    // Leave the widget at rest rather than mid-selection. Turbo restores a
    // cached page by putting the old nodes back, and a highlighted category
    // with no pointer near it is a claim about where the reader was looking.
    this.hoveredIndex = null
    this.pinnedIndex = null
    this.render()
  }

  onPointerOver(event) {
    if (!this.hoverEnabled(event)) return

    const index = this.indexFor(event.target)
    // Moving off the marks and onto the caption, the legend or the table
    // heading rests the line. Only leaving a category counts, so a pointer
    // wandering the widget does not leave a stale figure behind it.
    this.hoveredIndex = index
    this.render()
  }

  onPointerLeave() {
    if (this.hoveredIndex === null) return

    this.hoveredIndex = null
    this.render()
  }

  // `click`, never `pointerdown`. WCAG 2.2 SC 2.5.2 Pointer Cancellation is
  // satisfied by the no down-event route: a click fires on the up event and
  // dragging off the target before lifting cancels it. A down-event handler
  // would also fight the horizontal scroll gesture of the box these marks sit
  // in, since the same finger does both.
  onClick(event) {
    const index = this.indexFor(event.target, "hit")
    if (index === null) return

    // Tapping the selected category again clears it. A selection with no way
    // out is the complaint every mobile chart collects.
    this.pinnedIndex = this.pinnedIndex === index ? null : index
    this.render()
    this.follow(0)
  }

  // A linked chart's selection is the list's state, written in the address
  // bar, so a click anywhere else on the page is not a reason to drop it: the
  // reader paging through that month's rows is clicking inside the list.
  onDocumentClick(event) {
    if (this.linked()) return
    if (this.pinnedIndex === null) return
    if (this.element.contains(event.target)) return

    this.pinnedIndex = null
    this.render()
  }

  onKeyDown(event) {
    if (event.key !== "Escape") return
    if (this.pinnedIndex === null && this.hoveredIndex === null) return
    // The same reason as a click elsewhere: on a linked chart Escape belongs
    // to whatever has focus, and only clears the selection from inside it.
    if (this.linked() && !this.element.contains(document.activeElement)) return

    const cleared = this.pinnedIndex !== null
    this.hoveredIndex = null
    this.pinnedIndex = null
    this.render()
    if (cleared) this.follow(0)
  }

  // The keyboard half of the selection a tap already makes. Nothing here is the
  // only route to a figure: the table is one Tab away and holds every one of
  // them in words, so a browser that never runs this file loses a convenience
  // and no information.
  onTrackKeyDown(event) {
    // A modified arrow is somebody else's shortcut.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

    const last = this.hitTargets.length - 1
    // A chart with no categories keeps its native scrolling, which on an empty
    // plot is all there is to keep.
    if (last < 0) return

    const index = this.keyedIndex(event.key, last)
    // Every key this file does not handle leaves with its default intact,
    // which is most of the keyboard.
    if (index === null) return

    // The only prevented default in this file, and it sits below every return
    // above, so a key is taken only once the band step that replaces it is
    // certain to happen and to scroll the track itself.
    event.preventDefault()
    this.select(index)
    this.follow(this.constructor.FOLLOW_DELAY)
  }

  // Which category a key asks for, and null for a key that is not ours.
  keyedIndex(key, last) {
    if (key === this.constructor.FIRST_KEY) return 0
    if (key === this.constructor.LAST_KEY) return last

    const step = this.constructor.STEPS[key]
    if (step === undefined) return null
    // The first press takes the category the track opened on, rather than
    // stepping from an end that is off screen behind the reader.
    if (this.pinnedIndex === null) return last

    return this.clamp(this.pinnedIndex + step, last)
  }

  // Selects a category and brings its band into view. `nearest` on both axes,
  // so a step that is already visible scrolls nothing and the page is never
  // hauled about to centre a chart the reader can see perfectly well.
  select(index) {
    this.pinnedIndex = index
    this.render()

    this.scrollToIndex(index)
  }

  scrollToIndex(index) {
    const hit = this.hitTargets.find((candidate) => this.indexOn(candidate) === index)
    hit?.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  // --- categories that lead somewhere -------------------------------------------

  linked() {
    return this.hasFrameValue && this.frameValue !== ""
  }

  // Loads the selected category's address into the frame, or the address with
  // no category when the selection was cleared. The frame carries
  // `data-turbo-action="advance"`, so the address bar follows and Back undoes
  // a selection the way it undoes any other filter.
  follow(delay) {
    if (!this.linked()) return

    clearTimeout(this.followTimer)
    this.followTimer = setTimeout(() => {
      const frame = document.getElementById(this.frameValue)
      if (!frame) return

      const href = this.hrefFor(this.pinnedIndex)
      if (!href) return

      // Through Turbo rather than by setting `src`, because only a visit is
      // promoted to an address bar entry: a bare `src` loads the frame and
      // leaves the URL, and Back, describing the old month.
      if (window.Turbo?.visit) {
        window.Turbo.visit(href, { frame: this.frameValue, action: "advance" })
      } else {
        frame.src = href
      }
    }, delay)
  }

  hrefFor(index) {
    if (index !== null) {
      const hit = this.hitTargets.find((candidate) => this.indexOn(candidate) === index)
      return hit?.dataset.chartHref ?? null
    }

    const url = new URL(window.location.href)
    url.searchParams.delete(this.paramValue)
    url.searchParams.delete("page")
    return url.pathname + url.search
  }

  // Something other than the chart moved the list: "Show all months", a page
  // link, Back. The chart reads which category the list now shows from the
  // frame's own address rather than keeping a second copy of it.
  onFrameLoad(event) {
    if (event.target.id !== this.frameValue) return

    const key = new URL(event.target.src || window.location.href, window.location.href)
      .searchParams.get(this.paramValue)
    const hit = key ? this.hitTargets.find((candidate) => candidate.dataset.chartKey === key) : null
    const index = hit ? this.indexOn(hit) : null
    if (index === this.pinnedIndex) return

    this.pinnedIndex = index
    this.render()
  }

  // --- paging the track -----------------------------------------------------------

  connectPager() {
    if (!this.hasPagerTarget || !this.hasTrackTarget) return

    this.pagedTrack = this.trackTarget
    this.pager = this.pagerTarget
    this.onPagerClick = this.onPagerClick.bind(this)
    this.updatePager = this.updatePager.bind(this)
    this.pager.addEventListener("click", this.onPagerClick)
    this.pagedTrack.addEventListener("scroll", this.updatePager, { passive: true })
    window.addEventListener("resize", this.updatePager)
    this.updatePager()
  }

  disconnectPager() {
    if (!this.pagedTrack) return

    this.pager.removeEventListener("click", this.onPagerClick)
    this.pagedTrack.removeEventListener("scroll", this.updatePager)
    window.removeEventListener("resize", this.updatePager)
    this.pagedTrack = null
    this.pager = null
  }

  // Most of a box at a time, so the month a reader was looking at stays on
  // screen at the edge it moved to and the step reads as a continuation.
  onPagerClick(event) {
    const button = event.target.closest("[data-chart-step]")
    if (!button) return

    const step = Number.parseInt(button.dataset.chartStep, 10)
    this.pagedTrack.scrollBy({ left: step * this.pagedTrack.clientWidth * 0.8 })
  }

  // One arrow for each way there is somewhere to go: at the newest end only
  // the left one, at the oldest only the right, both in between, and neither
  // when the whole window fits.
  //
  // The track opens on its right hand end because it is `direction: rtl`, and
  // in that direction `scrollLeft` runs from nought at the right to a negative
  // number at the left. Measured from the left either way, so one comparison
  // serves both directions.
  updatePager() {
    const track = this.pagedTrack
    if (!track) return

    const overflow = track.scrollWidth - track.clientWidth
    const rtl = getComputedStyle(track).direction === "rtl"
    const fromLeft = rtl ? overflow + track.scrollLeft : track.scrollLeft

    this.showArrow(this.earlierTarget, overflow > 1 && fromLeft > 1)
    this.showArrow(this.laterTarget, overflow > 1 && fromLeft < overflow - 1)
  }

  // An arrow that disappears under the keyboard focus would drop the focus to
  // the top of the document, so it is handed to the track first: the reader
  // stays on the picture they were paging, at its end.
  showArrow(button, shown) {
    if (!shown && document.activeElement === button) this.pagedTrack.focus({ preventScroll: true })
    button.hidden = !shown
  }

  clamp(index, last) {
    if (index < 0) return 0

    return index > last ? last : index
  }

  // The pointer wins over the selection while it is on a category, so a mouse
  // user who has also clicked one reads what they are pointing at. Leaving the
  // marks falls back to the selection, and leaving both falls back to the
  // sentence the server put in `data-chart-resting-value`.
  render() {
    const index = this.hoveredIndex === null ? this.pinnedIndex : this.hoveredIndex
    const readout = this.readoutElement()

    if (readout) {
      const figures = index === null ? null : this.figuresFor(index)

      readout.textContent = figures === null ? this.restingValue : figures
      readout.classList.toggle(this.constructor.READING_CLASS, figures !== null)
    }

    this.focus(index)
    this.paint()
  }

  // The summary block, showing the pointed-at category in its own layout, and
  // what the server sent when nothing is pointed at. Read off the row's data
  // rather than its cells, so the block gets the total and the brief and not
  // the whole breakdown the table carries.
  focus(index) {
    const row = index === null ? null : this.rowTargets.find((candidate) => this.indexOn(candidate) === index)
    const reading = row ? { Label: row.dataset.chartLabel, Figure: row.dataset.chartFigure,
                            Note: row.dataset.chartBrief ?? "" } : null

    const slots = this.focusSlots ?? []

    slots.forEach((slot) => {
      slot.element.textContent = reading ? reading[slot.name] ?? "" : slot.resting
      slot.element.classList.toggle(this.constructor.READING_CLASS, reading !== null)
    })
  }

  // The live target while there is one, and the element connect found when
  // there is not. Same shape as `details_toggle_controller.js#summaryElement`,
  // and for the same reason: a lookup that has gone away must not turn a
  // teardown into a half-done one.
  readoutElement() {
    return this.hasReadoutTarget ? this.readoutTarget : this.readout
  }

  // This file says which category is which and nothing about how it reads. One
  // pass over everything stamped with an index, so a category that grew a second
  // column segment, a ring or an axis label is lit whole without this file being
  // told it happened.
  //
  // `querySelectorAll` on the root rather than a target list, because the
  // component stamps marks that are not targets, and because it is still
  // answerable during `disconnect`, where the target lookups are not and the
  // widget still has to be put back the way the server sent it.
  paint() {
    const active = this.constructor.ACTIVE_CLASS
    const pinned = this.constructor.PINNED_CLASS

    this.element.querySelectorAll("[data-chart-index]").forEach((node) => {
      const index = this.indexOn(node)
      const selected = index !== null && index === this.pinnedIndex

      node.classList.toggle(active, index !== null && index === this.hoveredIndex)
      node.classList.toggle(pinned, selected)

      // An aside the server shipped hidden exists for the selected category and
      // for no other, which on the axis is what gives a label to the categories
      // between the labelled ones. Touched only if it was hidden when this
      // controller arrived, so nothing else on the page is revealed by it.
      if (this.asides.has(node)) node.hidden = !selected
    })
  }

  // The elements a category owns that the server sent hidden. A plain Set of
  // nodes rather than a selector re-run later, for the reason at the top: after
  // the first reveal the attribute no longer answers the question.
  hiddenIndexedElements() {
    return new Set(this.element.querySelectorAll("[data-chart-index][hidden]"))
  }

  // The one statement of this category's figures, read off the row the table
  // rendered. Returns null when there is no such row, and null shows the
  // resting sentence: a chart with nothing to quote says nothing rather than
  // inventing a line.
  figuresFor(index) {
    const row = this.rowTargets.find((candidate) => this.indexOn(candidate) === index)
    if (!row) return null

    const fields = Array.from(row.querySelectorAll(":scope > th, :scope > td"))
      .map((cell) => cell.textContent.replace(/\s+/g, " ").trim())
      .filter((text) => text.length > 0)

    return fields.length > 0 ? fields.join(this.constructor.SEPARATOR) : null
  }

  // Walks up from whatever was under the pointer to the nearest element
  // carrying an index, so a pointer over a table cell finds its row and a
  // pointer over a mark finds its slot. `role` narrows it to one kind of
  // target, which is how a click selects only from the hit layer.
  indexFor(node, role = null) {
    const element = node instanceof Element ? node : node?.parentElement
    const carrier = element?.closest("[data-chart-index]")
    if (!carrier || !this.element.contains(carrier)) return null
    if (role && !this.isTarget(carrier, role)) return null

    return this.indexOn(carrier)
  }

  indexOn(node) {
    const index = Number.parseInt(node.dataset.chartIndex, 10)

    return Number.isInteger(index) ? index : null
  }

  isTarget(node, role) {
    return (node.getAttribute("data-chart-target") || "").split(/\s+/).includes(role)
  }

  // Read at the moment of the event rather than cached, so a laptop that gains
  // a touch screen, or a tablet that gains a trackpad, is answered as it is
  // now. `pointerType` is the second half: a touch that arrives through a
  // device that also reports a fine pointer still must not hover.
  hoverEnabled(event) {
    if (event.pointerType === "touch") return false

    return this.hoverQuery.matches
  }
}
